import React, { useRef, useEffect, useCallback, useState } from "react";

interface AuthCodeInputProps {
  length?: number;
  allowedCharacters?: "numeric" | "alpha" | "alphanumeric";
  containerClassName?: string;
  inputClassName?: string;
  onChange?: (value: string) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  placeholder?: string;
  isPassword?: boolean;
}

const patternMap = {
  numeric: /^[0-9]$/,
  alpha: /^[a-zA-Z]$/,
  alphanumeric: /^[a-zA-Z0-9]$/,
};

/**
 * OTP-инпут с поддержкой:
 * - iOS Safari автозаполнения SMS (через скрытый overlay input)
 * - Android Chrome WebOTP API
 * - Ручной вставки (Ctrl+V / Cmd+V)
 * - Посимвольного ввода
 */
export const AuthCodeInput: React.FC<AuthCodeInputProps> = ({
  length = 4,
  allowedCharacters = "alphanumeric",
  containerClassName,
  inputClassName,
  onChange,
  autoFocus = true,
  disabled = false,
  placeholder,
  isPassword = false,
}) => {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
  const hiddenRef = useRef<HTMLInputElement | null>(null);
  const pattern = patternMap[allowedCharacters];
  const [hiddenActive, setHiddenActive] = useState(true);

  const sendResult = useCallback(() => {
    const result = inputsRef.current
      .map((input) => input?.value || "")
      .join("");
    onChange?.(result);
  }, [onChange]);

  const fillDigits = useCallback(
    (digits: string, startIndex: number = 0) => {
      let idx = startIndex;
      for (let i = 0; i < digits.length && idx < length; i++) {
        const char = digits[i];
        if (pattern.test(char) && inputsRef.current[idx]) {
          inputsRef.current[idx]!.value = char;
          idx++;
        }
      }
      if (idx >= length) {
        inputsRef.current[length - 1]?.blur();
      } else {
        inputsRef.current[idx]?.focus();
      }
      sendResult();
    },
    [length, pattern, sendResult]
  );

  // Автозаполнение из скрытого overlay-input (iOS Safari)
  const handleHiddenChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const digits = value.replace(/\D/g, "");
      if (digits.length > 0) {
        setHiddenActive(false);
        fillDigits(digits, 0);
      }
    },
    [fillDigits]
  );

  useEffect(() => {
    if (autoFocus) {
      // На iOS фокус на скрытый input чтобы показалась подсказка автозаполнения
      if (hiddenRef.current && hiddenActive) {
        hiddenRef.current.focus();
      } else if (inputsRef.current[0]) {
        inputsRef.current[0].focus();
      }
    }
  }, [autoFocus, hiddenActive]);

  // WebOTP API для Android Chrome
  useEffect(() => {
    if (!("OTPCredential" in window)) return;

    const ac = new AbortController();

    (navigator.credentials as any)
      .get({
        otp: { transport: ["sms"] },
        signal: ac.signal,
      })
      .then((otp: any) => {
        if (otp?.code) {
          const digits = otp.code.replace(/\D/g, "");
          if (digits.length > 0) {
            setHiddenActive(false);
            fillDigits(digits, 0);
          }
        }
      })
      .catch(() => {
        // WebOTP не поддерживается или отменён — не проблема
      });

    return () => ac.abort();
  }, [fillDigits]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
      const value = e.target.value;

      // Автозаполнение — пришло больше 1 символа
      if (value.length > 1) {
        const digits = value.replace(
          allowedCharacters === "numeric" ? /\D/g : /[^a-zA-Z0-9]/g,
          ""
        );
        e.target.value = "";
        fillDigits(digits, index);
        return;
      }

      // Одиночный символ
      if (value && pattern.test(value)) {
        e.target.value = value;
        if (index < length - 1) {
          inputsRef.current[index + 1]?.focus();
        } else {
          inputsRef.current[index]?.blur();
        }
      } else {
        e.target.value = "";
      }

      sendResult();
    },
    [allowedCharacters, fillDigits, length, pattern, sendResult]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
      const target = e.target as HTMLInputElement;

      if (e.key === "Backspace") {
        if (target.value === "") {
          if (index > 0 && inputsRef.current[index - 1]) {
            inputsRef.current[index - 1]!.value = "";
            inputsRef.current[index - 1]!.focus();
            e.preventDefault();
          }
        } else {
          target.value = "";
        }
        sendResult();
      }
    },
    [sendResult]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>, index: number) => {
      e.preventDefault();
      const pastedValue = e.clipboardData.getData("text");
      const digits = pastedValue.replace(
        allowedCharacters === "numeric" ? /\D/g : /[^a-zA-Z0-9]/g,
        ""
      );
      if (digits.length > 0) {
        fillDigits(digits, index);
      }
    },
    [allowedCharacters, fillDigits]
  );

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      // Скрываем overlay при фокусе на реальный input
      setHiddenActive(false);
      e.target.select();
    },
    []
  );

  return (
    <div className={containerClassName} style={{ position: "relative" }}>
      {/* Скрытый overlay-input для перехвата iOS автозаполнения SMS */}
      {hiddenActive && (
        <input
          ref={hiddenRef}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          aria-hidden="true"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            opacity: 0,
            zIndex: 1,
            caretColor: "transparent",
            fontSize: "16px",
          }}
          onChange={handleHiddenChange}
          onKeyDown={() => {
            // При ручном вводе переключаемся на обычные inputs
            setHiddenActive(false);
            setTimeout(() => inputsRef.current[0]?.focus(), 0);
          }}
        />
      )}

      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => {
            inputsRef.current[i] = el;
          }}
          type={isPassword ? "password" : allowedCharacters === "numeric" ? "tel" : "text"}
          inputMode={allowedCharacters === "numeric" ? "numeric" : "text"}
          maxLength={length}
          className={inputClassName}
          autoComplete={i === 0 ? "one-time-code" : "off"}
          aria-label={`Цифра ${i + 1}`}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => handleChange(e, i)}
          onKeyDown={(e) => handleKeyDown(e, i)}
          onPaste={(e) => handlePaste(e, i)}
          onFocus={handleFocus}
        />
      ))}
    </div>
  );
};

export default AuthCodeInput;
